import { t } from "../i18n";
export type WalletPassColor =
  | "green"
  | "black"
  | "white"
  | "purple"
  | "blue"
  | "gold"
  | "custom";

export interface WalletPassTheme {
  background: string;
  text: string;
  accent: string;
  qrLight: string;
  qrDark: string;
  centerLogoBg: string;
  centerLogoFg: string;
  appleWalletStyle: "light" | "dark";
}

// "custom" entry is a placeholder so this `Record<WalletPassColor, ...>`
// covers every key in the union. At runtime, custom-color passes are routed
// through `themeFromHex(customColor)` in `resolveTheme` and never look this up.
export const WALLET_PASS_THEMES: Record<WalletPassColor, WalletPassTheme> = {
  green:  { background: "#24C961", text: "#000000", accent: "#1DA550", qrLight: "#FFFFFF", qrDark: "#000000", centerLogoBg: "#24C961", centerLogoFg: "#000000", appleWalletStyle: "light" },
  black:  { background: "#0A0A0A", text: "#FFFFFF", accent: "#262626", qrLight: "#FFFFFF", qrDark: "#000000", centerLogoBg: "#0A0A0A", centerLogoFg: "#24C961", appleWalletStyle: "dark"  },
  white:  { background: "#F5F5F5", text: "#000000", accent: "#D4D4D4", qrLight: "#FFFFFF", qrDark: "#000000", centerLogoBg: "#F5F5F5", centerLogoFg: "#000000", appleWalletStyle: "light" },
  purple: { background: "#7C3AED", text: "#FFFFFF", accent: "#5B21B6", qrLight: "#FFFFFF", qrDark: "#000000", centerLogoBg: "#7C3AED", centerLogoFg: "#FFFFFF", appleWalletStyle: "dark"  },
  blue:   { background: "#2563EB", text: "#FFFFFF", accent: "#1D4ED8", qrLight: "#FFFFFF", qrDark: "#000000", centerLogoBg: "#2563EB", centerLogoFg: "#FFFFFF", appleWalletStyle: "dark"  },
  gold:   { background: "#D4A017", text: "#000000", accent: "#A87E0F", qrLight: "#FFFFFF", qrDark: "#000000", centerLogoBg: "#D4A017", centerLogoFg: "#000000", appleWalletStyle: "light" },
  custom: { background: "#24C961", text: "#000000", accent: "#1DA550", qrLight: "#FFFFFF", qrDark: "#000000", centerLogoBg: "#24C961", centerLogoFg: "#000000", appleWalletStyle: "light" },
};

export const WALLET_PASS_COLOR_OPTIONS: { value: WalletPassColor; label: string; swatch: string }[] = [
  { value: "green",  label: t("walletPassRenderer.green"),  swatch: WALLET_PASS_THEMES.green.background },
  { value: "black",  label: t("walletPassRenderer.black"),  swatch: WALLET_PASS_THEMES.black.background },
  { value: "white",  label: t("walletPassRenderer.white"),  swatch: WALLET_PASS_THEMES.white.background },
  { value: "purple", label: t("walletPassRenderer.purple"), swatch: WALLET_PASS_THEMES.purple.background },
  { value: "blue",   label: t("walletPassRenderer.blue"),   swatch: WALLET_PASS_THEMES.blue.background },
  { value: "gold",   label: t("walletPassRenderer.gold"),   swatch: WALLET_PASS_THEMES.gold.background },
];

export interface BuildPassDesignOpts {
  color: WalletPassColor;
  customColor?: string | null;
  djName: string;
  qrImage: HTMLImageElement | string;
  rLogo?: HTMLImageElement | string | null;          // brand R — used for corner watermark + center fallback (tinted)
  customCenterLogo?: HTMLImageElement | string | null; // user-uploaded image — drawn as-is in QR center
  width?: number;
}

const ASPECT_W = 1500;
const ASPECT_H = 1000;

export async function buildPassDesign(opts: BuildPassDesignOpts): Promise<HTMLCanvasElement> {
  const width = opts.width ?? ASPECT_W;
  const height = Math.round((width * ASPECT_H) / ASPECT_W);
  const theme = resolveTheme(opts.color, opts.customColor);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d")!;

  // Solid background
  ctx.fillStyle = theme.background;
  ctx.fillRect(0, 0, width, height);

  // Subtle wave lines on the left side
  drawWaves(ctx, width, height, theme.accent);

  // Faint oversized R watermark bleeding off the top-right corner (brand texture),
  // tinted to the theme accent. The same R is reused for the QR centre below.
  const rLogoImg = opts.rLogo ? await loadImage(opts.rLogo) : null;
  if (rLogoImg) {
    drawCornerR(ctx, width, height, rLogoImg, theme.accent);
  }

  // Big white rounded square holding the QR. Sized so it never overlaps the
  // top "REQUEST" or bottom DJ-name labels.
  const qrBoxSize = Math.round(height * 0.62);
  const qrBoxX = (width - qrBoxSize) / 2;
  const qrBoxY = (height - qrBoxSize) / 2;
  const qrBoxRadius = Math.round(qrBoxSize * 0.06);

  ctx.fillStyle = theme.qrLight;
  roundRectPath(ctx, qrBoxX, qrBoxY, qrBoxSize, qrBoxSize, qrBoxRadius);
  ctx.fill();

  // QR code inside white box
  const qrPadding = Math.round(qrBoxSize * 0.05);
  const qrSize = qrBoxSize - qrPadding * 2;
  const qrImg = await loadImage(opts.qrImage);
  ctx.drawImage(qrImg, qrBoxX + qrPadding, qrBoxY + qrPadding, qrSize, qrSize);

  // Center logo over the QR. Two cases:
  //  - User uploaded a custom logo: draw it AS-IS on a white rounded square,
  //    so colors / photos stay intact (no tinting).
  //  - Otherwise: brand-R brush stroke, tinted to the theme's foreground color
  //    on a theme-colored rounded square.
  const customLogoImg = opts.customCenterLogo
    ? await loadImage(opts.customCenterLogo)
    : null;

  if (customLogoImg) {
    // 30% larger than the brand R sizing for better presence
    const logoSize = qrSize * 0.286;
    const logoX = qrBoxX + qrBoxSize / 2 - logoSize / 2;
    const logoY = qrBoxY + qrBoxSize / 2 - logoSize / 2;
    const logoRadius = logoSize * 0.22;

    ctx.fillStyle = "#FFFFFF";
    roundRectPath(ctx, logoX, logoY, logoSize, logoSize, logoRadius);
    ctx.fill();

    const inner = logoSize * 0.84;
    const innerOffset = (logoSize - inner) / 2;
    ctx.save();
    roundRectPath(ctx, logoX, logoY, logoSize, logoSize, logoRadius);
    ctx.clip();
    ctx.drawImage(
      customLogoImg,
      logoX + innerOffset,
      logoY + innerOffset,
      inner,
      inner
    );
    ctx.restore();
  } else if (rLogoImg) {
    const logoSize = qrSize * 0.176;
    const logoX = qrBoxX + qrBoxSize / 2 - logoSize / 2;
    const logoY = qrBoxY + qrBoxSize / 2 - logoSize / 2;
    const logoRadius = logoSize * 0.22;

    ctx.fillStyle = theme.centerLogoBg;
    roundRectPath(ctx, logoX, logoY, logoSize, logoSize, logoRadius);
    ctx.fill();

    const inner = logoSize * 0.78;
    const innerOffset = (logoSize - inner) / 2;
    const tinted = tintImage(rLogoImg, inner, inner, theme.centerLogoFg);
    ctx.drawImage(tinted, logoX + innerOffset, logoY + innerOffset);
  }

  // Top brand wordmark — replaces the old "REQUEST" text.
  // Always the original wordmark (white fill + black keyline). It reads on every
  // pass colour — light, mid, super-dark, and pure black (the keyline just fades
  // into the black, leaving clean white letters) — so no inversion is needed.
  const wordmark = await loadImage("/request-wordmark.png");
  // Sized to read at the DJ-name's visual height, vertically centred in the band
  // above the QR box (equidistant top ↔ QR).
  const wmH = Math.round(height * 0.165);
  const wmW = Math.round(wmH * (wordmark.naturalWidth / wordmark.naturalHeight));
  ctx.drawImage(
    wordmark,
    Math.round(width / 2 - wmW / 2),
    Math.round(height * 0.0125),
    wmW,
    wmH
  );

  // Bottom DJ name — optically centred in the band between the QR box and the
  // bottom edge, so it sits equidistant from the QR and the pass bottom.
  const djName = (opts.djName || "DJ").trim().toUpperCase();
  const qrBoxBottom = qrBoxY + qrBoxSize;
  drawCenteredText(ctx, djName, {
    x: width / 2,
    y: (qrBoxBottom + height) / 2,
    color: theme.text,
    fontSize: Math.round(height * 0.115),
    maxWidth: width * 0.82,
  });

  return canvas;
}

function drawCenteredText(
  ctx: CanvasRenderingContext2D,
  text: string,
  opts: { x: number; y: number; color: string; fontSize: number; maxWidth: number }
) {
  ctx.save();
  ctx.fillStyle = opts.color;
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";

  let fontSize = opts.fontSize;
  ctx.font = fontStack(fontSize);
  while (fontSize > 28 && ctx.measureText(text).width > opts.maxWidth) {
    fontSize -= 4;
    ctx.font = fontStack(fontSize);
  }

  // Optically centre the glyph box on opts.y (all-caps names sit visually
  // centred, not em-box centred which leaves them looking too high).
  const m = ctx.measureText(text);
  const baselineY =
    opts.y + (m.actualBoundingBoxAscent - m.actualBoundingBoxDescent) / 2;
  ctx.fillText(text, opts.x, baselineY);
  ctx.restore();
}

function fontStack(size: number): string {
  return `900 ${size}px "Helvetica Neue", "Helvetica", "Arial Black", Arial, sans-serif`;
}

function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  const rad = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rad, y);
  ctx.lineTo(x + w - rad, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rad);
  ctx.lineTo(x + w, y + h - rad);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rad, y + h);
  ctx.lineTo(x + rad, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rad);
  ctx.lineTo(x, y + rad);
  ctx.quadraticCurveTo(x, y, x + rad, y);
  ctx.closePath();
}

function drawWaves(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  color: string
) {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(2, width * 0.0025);
  ctx.globalAlpha = 0.4;
  ctx.lineCap = "round";

  const waveSpan = width * 0.45;
  for (let i = 0; i < 7; i++) {
    const y0 = height * 0.55 + i * (height * 0.04);
    ctx.beginPath();
    ctx.moveTo(-20, y0);
    for (let x = 0; x <= waveSpan; x += 12) {
      const phase = (x + i * 60) / (width * 0.08);
      const y = y0 - Math.sin(phase) * (height * 0.025);
      ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  ctx.restore();
}

function drawCornerR(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  rLogo: HTMLImageElement,
  accentColor: string
) {
  ctx.save();
  ctx.globalAlpha = 0.5;
  const size = Math.round(height * 1.05);
  const x = width - size * 0.65;
  const y = -size * 0.08;
  const tinted = tintImage(rLogo, size, size, accentColor);
  ctx.drawImage(tinted, x, y);
  ctx.restore();
}

function tintImage(
  img: HTMLImageElement,
  w: number,
  h: number,
  color: string
): HTMLCanvasElement {
  const off = document.createElement("canvas");
  off.width = w;
  off.height = h;
  const ctx = off.getContext("2d")!;

  ctx.drawImage(img, 0, 0, w, h);

  // Convert near-white pixels to transparent so we can tint the brush strokes
  // without painting the whole image rectangle. r.png has a white background;
  // this turns it into a clean silhouette.
  const imageData = ctx.getImageData(0, 0, w, h);
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const lum = (r + g + b) / 3;
    if (lum > 235) {
      data[i + 3] = 0;
    } else if (lum > 200) {
      data[i + 3] = Math.round((255 - lum) * 7);
    }
  }
  ctx.putImageData(imageData, 0, 0);

  // Recolor remaining pixels to the tint
  ctx.globalCompositeOperation = "source-in";
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, w, h);
  ctx.globalCompositeOperation = "source-over";

  return off;
}

/**
 * Resolve the theme. Accepts either a preset key OR a custom hex (overrides).
 */
export function resolveTheme(color: WalletPassColor, customColor?: string | null): WalletPassTheme {
  if ((color === "custom" || customColor) && customColor && /^#[0-9a-fA-F]{6}$/.test(customColor)) {
    return themeFromHex(customColor);
  }
  return WALLET_PASS_THEMES[color] || WALLET_PASS_THEMES.green;
}

function themeFromHex(hex: string): WalletPassTheme {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  // sRGB luminance
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  const isDarkText = lum > 0.6;
  const text = isDarkText ? "#000000" : "#FFFFFF";

  // Accent = darken/lighten background by 18% for the corner R + waves
  const factor = isDarkText ? 0.82 : 1.18;
  const ar = Math.max(0, Math.min(255, Math.round(r * factor)));
  const ag = Math.max(0, Math.min(255, Math.round(g * factor)));
  const ab = Math.max(0, Math.min(255, Math.round(b * factor)));
  const accent = `#${ar.toString(16).padStart(2, "0")}${ag.toString(16).padStart(2, "0")}${ab.toString(16).padStart(2, "0")}`;

  return {
    background: hex,
    text,
    accent,
    qrLight: "#FFFFFF",
    qrDark: "#000000",
    centerLogoBg: hex,
    centerLogoFg: text,
    appleWalletStyle: isDarkText ? "light" : "dark",
  };
}

async function loadImage(src: HTMLImageElement | string): Promise<HTMLImageElement> {
  if (src instanceof HTMLImageElement) {
    if (src.complete && src.naturalWidth > 0) return src;
    return new Promise((resolve, reject) => {
      src.onload = () => resolve(src);
      src.onerror = reject;
    });
  }
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * Rasterize a react-qr-code SVG element into a square PNG data URL, sized for
 * the pass renderer. Shared by every surface that turns an on-page QR into a
 * printable design (DJ QR page, admin venue QRs).
 */
export function qrSvgToPngDataUrl(svgEl: SVGSVGElement, size = 1200): Promise<string> {
  return new Promise((resolve) => {
    const serialized = new XMLSerializer().serializeToString(svgEl);
    const blob = new Blob([serialized], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d")!;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/png"));
    };
    img.src = url;
  });
}
